import { landmineNames } from '@mm/constants/landmine-names';
import { EnhancedOutgoingMessagesStatistic } from '@mm/state/model/ConnectedPageModel';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getPercentage } from '@mm/utils';
import monkeyPopUp from 'assets/monkey-pop-up.svg';
import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { PrimaryButton } from '../../../buttons/PrimaryButton/PrimaryButton';
import { PercentageBar } from '../../../components/PercentageBar/PercentageBar';
import BasicModalTemplate from '../../BasicModalTemplate';
import { useFreeTrialUpgradeModal } from '../../FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { ModalInstanceProps } from '../../interfaces';
import { useLandmineOpensHelpers } from '../../UniversalUpgradeModal/useLandmineOpensHelpers';
import './SendLimitNoticeTemplate.pcss';

type OuterProps = ModalInstanceProps & {
  noticeText: string | ReactNode;
  activeMessagesStatistic: EnhancedOutgoingMessagesStatistic;
  onUpgradeClick: () => any;
  upgradeButtonCaption?: string;
  hidePercentageBar?: boolean;
  type: string;
};

type InnerProps = ReturnType<typeof mapStateToProps>;

type Props = OuterProps & InnerProps;

export const getBucketedPercentage = (percentage: number) =>
  percentage <= 79
    ? 'under_80'
    : percentage > 79 && percentage < 90
    ? '80'
    : percentage >= 90 && percentage < 100
    ? '90'
    : '100';

const SendLimitNoticeTemplate = (props: Props) => {
  const {
    closeModal,
    noticeText,
    activeMessagesStatistic,
    onUpgradeClick,
    upgradeButtonCaption,
    hidePercentageBar,
    company,
    type,
  } = props;
  const isOwnerOrAdmin = company && company.editable;

  const usagePercentage = getPercentage(
    activeMessagesStatistic.count,
    activeMessagesStatistic.outgoingMessageLimit,
  );
  const bucketedPercentage = getBucketedPercentage(usagePercentage);
  const ownership = isOwnerOrAdmin ? 'OWNER_OR_ADMIN' : 'NOT_OWNER_OR_ADMIN';
  const { landmineOpenId, landmineOpensCRUDActions } = useLandmineOpensHelpers(
    `${landmineNames.SENDS_USAGE__WARNING}__${type}__${ownership}__${bucketedPercentage}`,
    '',
  );

  const { openFreeTrialUpgradeModal, isFreeTrialPromotionWithoutSubscription } =
    useFreeTrialUpgradeModal();

  return (
    <BasicModalTemplate
      className="send-limit-notice-template"
      withMonkeyBackground={false}
    >
      <div className="send-limit-notice-template__header-text">
        Send Credits Limit Notice
      </div>

      <div className="send-limit-notice-template__top-section">
        <div className="send-limit-notice-template__left-section">
          <div className="send-limit-notice-template__text">{noticeText}</div>

          {!hidePercentageBar && (
            <PercentageBar
              title="Send Credits used"
              total={activeMessagesStatistic.outgoingMessageLimit}
              used={activeMessagesStatistic.count}
              className="send-limit-notice-template__percentage-bar"
            />
          )}
        </div>

        <img
          src={monkeyPopUp}
          className="send-limit-notice-template__image"
          alt="Page over illustration"
        />
      </div>

      <div className="send-limit-notice-template__bottom-section">
        {isOwnerOrAdmin && (
          <>
            <div
              className="send-limit-notice-template__not-now-button"
              onClick={closeModal}
            >
              Not Now
            </div>

            <PrimaryButton
              onClick={() => {
                if (landmineOpenId) {
                  landmineOpensCRUDActions.update(landmineOpenId, true);
                }
                if (isFreeTrialPromotionWithoutSubscription) {
                  openFreeTrialUpgradeModal();
                } else {
                  onUpgradeClick();
                }
              }}
              className="send-limit-notice-template__upgrade-button"
            >
              {upgradeButtonCaption || 'UPGRADE'}
            </PrimaryButton>
          </>
        )}

        {!isOwnerOrAdmin && (
          <div className="send-limit-notice-template__no-admin-footer">
            <div className="send-limit-notice-template__non-admin-text">
              Only the account Owner or Admin can Upgrade Pages
            </div>

            <PrimaryButton
              onClick={closeModal}
              className="send-limit-notice-template__upgrade-button"
            >
              Ok
            </PrimaryButton>
          </div>
        )}
      </div>
    </BasicModalTemplate>
  );
};

const mapStateToProps = (state: StoreState) => ({
  company: activeCompanySelector(state),
});

const enhance = compose<InnerProps, OuterProps>(connect(mapStateToProps));

export default enhance(SendLimitNoticeTemplate);
